import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Copy, Trash2, ChevronRight, ChevronDown,
  Trophy, Flame, BookOpen, UserPlus, ClipboardList, Shield, Crown, Lock, Sparkles,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { useCoach, type Team, type TeamMember } from '@/hooks/useCoach';
import { useCoachSubscription } from '@/hooks/useCoachSubscription';
import { useAuth } from '@/hooks/useAuth';
import { type CoachTier } from '@/lib/stripeConfig';
import { toast } from 'sonner';
import { ContentHub } from '@/components/coach/ContentHub';
import { ParentInviteForm } from '@/components/coach/ParentInviteForm';
import { ReferralCard } from '@/components/coach/ReferralCard';
import TeamDashboardCard, { getHealthStatus, HEALTH_RING, type HealthStatus } from '@/components/coach/TeamDashboardCard';
import PlayerDetailExpanded from '@/components/coach/PlayerDetailExpanded';
import CoachOnboardingChecklist from '@/components/coach/CoachOnboardingChecklist';
import CoachTour from '@/components/coach/CoachTour';
import { CoachProComingSoonDialog } from '@/components/coach/CoachProComingSoonDialog';
import { Loader2 } from 'lucide-react';
import CoachHelpButton from '@/components/coach/CoachHelpButton';
import { useCoachTour, setCoachTourFlag } from '@/hooks/useCoachTour';
import { FaqSection } from '@/components/FaqSection';
import { ModeSwitcher } from '@/components/ModeSwitcher';
import { NotificationBell } from '@/components/coach/NotificationBell';
import { useAssignmentCompletion } from '@/hooks/useAssignmentCompletion';
import { SeoHead } from '@/components/SeoHead';
import { CoachBadges } from '@/components/coach/CoachBadges';
import { TeamActivityFeed } from '@/components/coach/TeamActivityFeed';
import { TeamShareCard } from '@/components/coach/TeamShareCard';


type View = 'teams' | 'team-detail';

const TIER_BADGE: Record<CoachTier, { label: string; color: string } | null> = {
  free: null,
  pro: { label: 'PRO', color: 'bg-accent text-accent-foreground' },
};

export default function CoachDashboard() {
  const { user, signOut } = useAuth();
  const {
    coachProfile, teams, loading, createTeam, deleteTeam,
    fetchTeamMembers, removeMember,
  } = useCoach();
  const { coachTier, limits, loading: subLoading, startCheckout, openCustomerPortal } = useCoachSubscription();

  const [view, setView] = useState<View>('teams');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [healthFilter, setHealthFilter] = useState<HealthStatus | null>(null);
  const { completion: assignmentCompletion, error: assignmentCompletionError } = useAssignmentCompletion(selectedTeam?.id);

  // Coach display name (never show raw email in the UI)
  const [coachName, setCoachName] = useState<string>('');
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .rpc('coach_display_name', { p_coach_user_id: user.id })
      .then(({ data }) => { if (!cancelled && typeof data === 'string') setCoachName(data); });
    return () => { cancelled = true; };
  }, [user]);


  // Create team
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamSport, setNewTeamSport] = useState(coachProfile?.primary_sport || 'football');

  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);

  // Whether this coach has ever assigned anything — drives "first lesson" copy.
  const [hasAssignments, setHasAssignments] = useState<boolean | null>(null);
  useEffect(() => {
    const ids = teams.map(t => t.id);
    if (!coachProfile) return;
    let cancelled = false;
    const check = async () => {
      const [la, cc] = await Promise.all([
        ids.length ? supabase.from('lesson_assignments').select('id', { count: 'exact', head: true }).in('team_id', ids) : Promise.resolve({ count: 0 } as any),
        supabase.from('coach_content').select('id', { count: 'exact', head: true }).eq('coach_id', coachProfile.id).not('team_id', 'is', null),
      ]);
      if (!cancelled) setHasAssignments(((la as any).count ?? 0) + ((cc as any).count ?? 0) > 0);
    };
    check();
    window.addEventListener('assignment-completion-changed', check);
    return () => { cancelled = true; window.removeEventListener('assignment-completion-changed', check); };
  }, [coachProfile, teams]);

  const [signingOut, setSigningOut] = useState(false);
  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try { await signOut(); } finally { setSigningOut(false); }
  };

  const [proDialogOpen, setProDialogOpen] = useState(false);

  const canCreateTeam = limits.teams === -1 || teams.length < limits.teams;

  // Guided onboarding tour
  const { complete: completeTour, skip: skipTour, reset: resetTour } = useCoachTour();
  const [tourOpen, setTourOpen] = useState(false);
  const tourLaunchedRef = useRef(false);
  useEffect(() => {
    if (tourLaunchedRef.current || loading || subLoading || !coachProfile) return;
    const assigning = sessionStorage.getItem('sportara-coach-assign-first') === '1'
      || sessionStorage.getItem('sportara-coach-open-picker') === '1';
    // Server-side state: auto-launch once per coach, right after the wizard, never again.
    const cp = coachProfile as { onboarding_completed_at?: string | null; tour_seen_at?: string | null };
    if (!cp.onboarding_completed_at || cp.tour_seen_at || assigning) return;
    tourLaunchedRef.current = true;
    setTourOpen(true);
    supabase.rpc('mark_coach_tour_seen' as any).then(() => {}, () => {});
  }, [loading, subLoading, coachProfile]);

  const openTeam = async (team: Team) => {
    setSelectedTeam(team);
    setView('team-detail');
    setCoachTourFlag('analyticsVisited');
    setMembersLoading(true);
    const m = await fetchTeamMembers(team.id);
    setMembers(m);
    setMembersLoading(false);
  };

  const openTeamById = (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    if (team) openTeam(team);
  };


  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    if (!canCreateTeam) {
      toast('Upgrade your Coach plan to create more teams', {
        action: { label: 'See Plans', onClick: () => setProDialogOpen(true) },
      });
      return;
    }
    try {
      await createTeam(newTeamName.trim(), newTeamSport);
      toast.success('Team created!');
      setNewTeamName('');
      setShowCreateTeam(false);
    } catch {
      toast.error('Failed to create team');
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    if (!confirm('Delete this team? All members will be removed.')) return;
    try {
      await deleteTeam(teamId);
      setView('teams');
      toast.success('Team deleted');
    } catch {
      toast.error('Failed to delete team');
    }
  };

  // Coach-initiated roster additions were removed: players must join with the
  // team join code (or a parent joins their child), so rostering requires consent.



  const handleRemoveMember = async (memberId: string) => {
    try {
      await removeMember(memberId);
      setMembers(prev => prev.filter(m => m.id !== memberId));
      toast.success('Player removed');
    } catch {
      toast.error('Failed to remove player');
    }
  };

  const copyJoinCode = async (code: string) => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/join/${encodeURIComponent(code)}`); setCoachTourFlag('codeShared'); toast.success('Team invite link copied!'); } catch { toast.error('Copy failed. Select the join code manually.'); }
  };
  // Returning coaches land straight on their team dashboard; the wizard's
  // "Assign your first lesson" also opens the Content Hub picker once mounted.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (loading || !teams.length || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    if (sessionStorage.getItem('sportara-coach-assign-first') === '1') {
      sessionStorage.removeItem('sportara-coach-assign-first');
      sessionStorage.setItem('sportara-coach-open-picker', '1');
      setTourOpen(false);
    }
    if (view === 'teams') openTeam(teams[0]);
  }, [loading, teams]);

  if (loading || subLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const sportEmoji = (sport: string) =>
    sport === 'basketball' ? '🏀' : sport === 'soccer' ? '⚽' : '🏈';

  const tierBadge = TIER_BADGE[coachTier];

  const upgradeBanner = coachTier === 'free' ? (
    <div className="flex items-center gap-3 rounded-xl px-4 py-3 ring-1 ring-primary/30 bg-gradient-to-r from-primary/15 to-violet-500/10">
      <Sparkles className="w-4 h-4 text-primary shrink-0" />
      <p className="flex-1 min-w-0 text-xs font-body text-foreground/90">
        <span className="font-display font-bold">Upgrade to Coach Pro</span>
        {' — '}unlock unlimited teams, assignments and analytics · $9.99/mo
      </p>
      <Button size="sm" onClick={() => setProDialogOpen(true)} className="font-display font-bold shrink-0">
        See Plans
      </Button>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-background premium-page-bg text-foreground">
      <SeoHead title="Coaches Hub | Sportara" description="Manage your Sportara teams, lessons, and player progress in Coaches Hub." path="/coaches/dashboard" noindex />
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background/70 backdrop-blur-md border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary shrink-0" />
                <h1 className="font-display font-bold text-lg truncate">Coaches Hub</h1>
                {tierBadge && (
                  <span className={`text-[10px] font-display font-bold px-1.5 py-0.5 rounded ${tierBadge.color}`}>
                    {tierBadge.label}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground font-body truncate">
                {coachProfile?.organization_name || coachName || 'Coach'}
              </p>
              <CoachBadges refreshKey={`${teams.length}-${members.length}-${assignmentCompletion.players.length}`} />
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="sm" className="font-display font-bold text-primary" onClick={() => document.getElementById('refer-a-coach')?.scrollIntoView({ behavior: 'smooth' })}>
              Refer a coach
            </Button>
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={handleSignOut} disabled={signingOut} aria-busy={signingOut} className="text-muted-foreground hover:text-foreground">
              {signingOut ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Signing you out…</>) : 'Sign Out'}
            </Button>
          </div>
        </div>
        <ModeSwitcher />
      </header>


      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Manage Billing — show for paid coaches */}
        {coachTier !== 'free' && (
          <Card className="p-4 border-primary/30 bg-primary/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <Crown className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-sm">Coach Pro</p>
                <p className="text-xs text-muted-foreground font-body">
                  Manage your subscription, payment method, or cancel anytime
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={openCustomerPortal} className="font-display font-bold shrink-0 border-2">
                Manage Billing
              </Button>
            </div>
          </Card>
        )}


        <ReferralCard />

        <AnimatePresence mode="wait">
          {view === 'teams' ? (
            <motion.div key="teams" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* Onboarding Checklist */}
              <CoachOnboardingChecklist teams={teams} coachId={coachProfile?.id} />

              {/* Teams List */}
              <div className="flex items-center justify-between mb-4 mt-4">
                <h2 className="font-display font-bold text-xl">Your Teams</h2>
                <Button
                  size="sm"
                  onClick={() => {
                    if (canCreateTeam) {
                      setShowCreateTeam(true);
                    } else {
                      toast('Upgrade your Coach plan for more teams', {
                        action: { label: 'See Plans', onClick: () => setProDialogOpen(true) },
                      });
                    }
                  }}
                  className="font-display font-bold"
                  variant={canCreateTeam ? 'default' : 'outline'}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Team
                  {!canCreateTeam && <Lock className="w-3 h-3 ml-1" />}
                </Button>
              </div>

              {/* Tier info */}
              {limits.teams !== -1 && teams.length > 0 && (
                <p className="text-[11px] text-muted-foreground font-body mb-3">
                  Free plan: {teams.length}/{limits.teams} team{limits.teams > 1 ? 's' : ''} used
                </p>
              )}

              {/* Create Team Modal */}
              {showCreateTeam && (
                <Card className="p-4 mb-4 space-y-3 border-primary/30">
                  <Input
                    placeholder="Team name"
                    value={newTeamName}
                    onChange={e => setNewTeamName(e.target.value)}
                    className="font-body"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    {['football', 'basketball', 'soccer'].map(s => (
                      <button
                        key={s}
                        onClick={() => setNewTeamSport(s)}
                        className={`flex-1 py-2 rounded-lg text-sm font-display font-bold border-2 transition-all ${
                          newTeamSport === s ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground'
                        }`}
                      >
                        {sportEmoji(s)} {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowCreateTeam(false)} className="flex-1">Cancel</Button>
                    <Button size="sm" onClick={handleCreateTeam} disabled={!newTeamName.trim()} className="flex-1">Create</Button>
                  </div>
                </Card>
              )}

              {teams.length === 0 ? (
                <Card className="p-8 text-center">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="font-display font-bold text-lg">No teams yet</p>
                  <p className="text-sm text-muted-foreground font-body mt-1">
                    Create your first team to start managing players
                  </p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {teams.map(team => (
                    <Card
                      key={team.id}
                      className="p-4 cursor-pointer hover:border-primary/30 transition-colors"
                      onClick={() => openTeam(team)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{sportEmoji(team.sport)}</span>
                          <div>
                            <h3 className="font-display font-bold">{team.name}</h3>
                            <p className="text-xs text-muted-foreground font-body">
                              Code: <span className="font-mono font-bold text-foreground">{team.join_code}</span>
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Coach FAQ */}
              <Card className="p-4 mt-6">
                <FaqSection
                  title="Coach FAQ"
                  items={[
                    { id: 'create-team', question: 'How do I create a team?', answer: 'Click the "New Team" button above, enter a team name, and pick the sport. Your team will get a unique join code that players can use to join from their Profile page.' },
                    { id: 'join-code', question: 'How do players join my team?', answer: 'Share your team\'s join code with your players. They can enter the code on their Profile page under "Join a Team." You can also search for players by username and add them directly.' },
                    { id: 'assign-lessons', question: 'How do I assign lessons to my team?', answer: 'Open a team, scroll down to the Content Hub, tap "Assign Lessons," and pick lessons from the catalog. Players will see a notification banner alerting them to new assignments, and assigned lessons are marked with a 📋 badge in their learning path.' },
                    { id: 'track-progress', question: 'Can I track my players\' progress?', answer: 'Yes! Each assignment shows a completion progress bar. Click on any player in the roster to see their XP, level, streak, and which lessons they\'ve finished. Team-wide averages are available on the Pro plan.' },
                    { id: 'tiers', question: 'What\'s included in each plan?', answer: 'Basic: 1 team, 6 unique active lessons, basic roster management. Coach Pro ($9.99/mo): unlimited teams, unlimited assignments, advanced analytics and completion tracking.' },
                    { id: 'remove-player', question: 'How do I remove a player from my team?', answer: 'Open the team, find the player in the roster, and click the trash icon next to their name. This only removes them from your team — it doesn\'t affect their personal account or progress.' },
                    { id: 'notifications', question: 'Do players get notified about new assignments?', answer: 'Yes! When you assign a lesson, players see a notification banner in their Learn tab the next time they open the app, telling them how many new lessons have been assigned.' },
                  ]}
                />
              </Card>
            </motion.div>
          ) : selectedTeam && (
            <motion.div key="detail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-5">
              <button
                onClick={() => { setView('teams'); setHealthFilter(null); }}
                className="text-xs font-display font-bold text-muted-foreground hover:text-foreground transition-colors"
              >
                ← All teams
              </button>
              {/* Team Hero Card */}
              <div className="relative overflow-hidden rounded-2xl glass-card shadow-elevated p-5">
                <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
                <div className="absolute right-3 top-3 text-7xl opacity-10 pointer-events-none select-none" aria-hidden>
                  {sportEmoji(selectedTeam.sport)}
                </div>
                <div className="relative flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl" aria-hidden>{sportEmoji(selectedTeam.sport)}</span>
                      <h2 className="font-display font-extrabold text-2xl text-foreground truncate">
                        {selectedTeam.name}
                      </h2>
                    </div>
                    <p className="text-xs text-muted-foreground font-body uppercase tracking-wider mb-3">
                      {selectedTeam.sport} · {members.length} {members.length === 1 ? 'player' : 'players'}
                    </p>
                    <button
                      data-tour="join-code"
                      onClick={() => copyJoinCode(selectedTeam.join_code)}
                      className="inline-flex items-center gap-2 rounded-xl bg-primary/15 hover:bg-primary/25 ring-1 ring-primary/30 px-4 py-3 transition-colors group"
                    >
                      <span className="text-[10px] font-body uppercase tracking-wider text-primary/80">Join code</span>
                      <span className="font-mono font-bold text-2xl tracking-wider text-foreground">{selectedTeam.join_code}</span>
                      <Copy className="w-3.5 h-3.5 text-primary group-hover:scale-110 transition-transform" />
                    </button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteTeam(selectedTeam.id)}
                    className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
                    aria-label="Delete team"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {upgradeBanner}

              {/* Roster nudge (no player cap exists on any plan, so only the "room to grow" variant applies) */}
              {!membersLoading && members.length < 5 && (
                <div className="flex flex-wrap items-center gap-3 rounded-xl px-4 py-3 ring-1 ring-primary/30 bg-primary/10">
                  <UserPlus className="w-4 h-4 text-primary shrink-0" />
                  <p className="flex-1 min-w-[12rem] text-sm font-body text-foreground/90">
                    Your roster has room — share your join code to add more players
                  </p>
                  <button
                    onClick={() => copyJoinCode(selectedTeam.join_code)}
                    className="inline-flex items-center gap-2 rounded-lg bg-background/60 px-3 py-2 ring-1 ring-primary/40 hover:bg-background"
                    aria-label={`Copy join link for code ${selectedTeam.join_code}`}
                  >
                    <span className="font-mono font-bold text-lg tracking-wider">{selectedTeam.join_code}</span>
                    <Copy className="w-4 h-4 text-primary" />
                  </button>
                </div>
              )}

              <TeamActivityFeed teamId={selectedTeam.id} />

              <TeamShareCard teamId={selectedTeam.id} teamName={selectedTeam.name} sport={selectedTeam.sport} />



              {/* Stats grid + Health pills */}
              <div data-tour="analytics">
                <TeamDashboardCard
                  members={members}
                  healthFilter={healthFilter}
                  onHealthFilterChange={setHealthFilter}
                  completion={assignmentCompletion}
                  completionError={assignmentCompletionError}
                />
              </div>

              {/* Getting Started checklist */}
              <CoachOnboardingChecklist teams={teams} coachId={coachProfile?.id} />

              {/* Invite players (consent-based join code) */}
              <div className="glass-card p-5 space-y-2" data-tour="add-players">
                <h3 className="font-display font-bold text-sm flex items-center gap-2 text-foreground">
                  <UserPlus className="w-4 h-4 text-primary" /> Add Players
                </h3>
                <p className="text-sm text-muted-foreground font-body">
                  Share your team join code with players (or their parent). They join from their own
                  account, so they always opt in before you can see their progress.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyJoinCode(selectedTeam.join_code)}
                  className="font-display font-bold"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copy invite link
                </Button>
              </div>

              <ParentInviteForm teamId={selectedTeam.id} />

              {/* Roster */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display font-bold text-foreground flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-primary" /> Roster ({healthFilter ? `${members.filter(m => getHealthStatus(m.last_active_date) === healthFilter).length} of ${members.length}` : members.length})
                  </h3>
                </div>

                {membersLoading ? (
                  <div className="text-center py-8">
                    <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
                  </div>
                ) : members.length === 0 ? (
                  <div className="glass-card p-8 text-center">
                    <Users className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground font-body">
                      No players yet. Copy the invite link above and send it to a parent or player. They can join without installing an app.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {members
                      .filter(m => !healthFilter || getHealthStatus(m.last_active_date) === healthFilter)
                      .map(member => {
                        const health = getHealthStatus(member.last_active_date);
                        const assignmentProgress = assignmentCompletion.players.find(
                          player => player.user_id === member.player_user_id,
                        );
                        const assignedDone = assignmentProgress?.completed ?? 0;
                        const assignedTotal = assignmentProgress?.assigned ?? 0;
                        const assignedPct = assignmentProgress?.pct ?? 0;
                        const isExpanded = expandedPlayerId === member.id;
                        return (
                          <div
                            key={member.id}
                            className="glass-card p-4 shadow-elevated transition-colors"
                          >
                            <button
                              onClick={() => setExpandedPlayerId(isExpanded ? null : member.id)}
                              aria-expanded={isExpanded}
                              className="w-full text-left flex items-center gap-3 hover:bg-white/[0.02] -m-1 p-1 rounded-lg transition-colors"
                            >
                              <div className={`w-11 h-11 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 ring-2 flex items-center justify-center text-sm font-display font-bold text-primary shrink-0 ${HEALTH_RING[health]}`}>
                                {(member.username || '?')[0].toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-display font-bold text-sm text-foreground truncate">
                                  {member.username || 'Unknown'}
                                </p>
                                <div className="flex items-center gap-3 text-[11px] text-muted-foreground font-body mt-0.5">
                                  <span className="flex items-center gap-1">
                                    <Trophy className="w-3 h-3 text-primary" /> {(member.xp || 0).toLocaleString()}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Flame className="w-3 h-3 text-warning" /> {member.streak || 0}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <BookOpen className="w-3 h-3 text-emerald-400" /> {member.completed_lessons?.length || 0}
                                  </span>
                                </div>
                                {assignedTotal > 0 && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                      <div
                                        className="h-full bg-gradient-to-r from-primary to-emerald-400 transition-all"
                                        style={{ width: `${assignedPct}%` }}
                                      />
                                    </div>
                                    <span className="text-[10px] font-display font-bold text-muted-foreground shrink-0">
                                      {assignedDone}/{assignedTotal}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <ChevronDown
                                  className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleRemoveMember(member.id); }}
                                  className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                  aria-label="Remove player"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </button>
                            {isExpanded && (
                              <PlayerDetailExpanded
                                member={member}
                                sport={selectedTeam.sport}
                                assignmentProgress={assignmentProgress}
                              />
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              <Button className="w-full sm:w-auto" onClick={() => { document.querySelector('[data-tour="content-hub"]')?.scrollIntoView({ behavior: 'smooth' }); window.dispatchEvent(new Event('coach-open-lesson-picker')); }}>{hasAssignments ? 'Assign lessons' : 'Assign your first lesson'}</Button>
              {/* Content Hub — lessons, videos & articles in one place */}
              {coachProfile && (
                <ContentHub
                  coachId={coachProfile.id}
                  teamId={selectedTeam.id}
                  sport={selectedTeam.sport}
                  members={members}
                  coachTier={coachTier}
                  onUpgrade={() => setProDialogOpen(true)}
                />
              )}



            </motion.div>
          )}

        </AnimatePresence>
      </div>

      <CoachTour
        open={tourOpen}
        onStart={() => { if (teams.length > 0 && view !== 'team-detail') openTeam(teams[0]); }}
        onSkip={() => { setTourOpen(false); skipTour(); }}
        onComplete={() => { setTourOpen(false); completeTour(); }}
        hasAssignments={!!hasAssignments}
        onAssignContent={() => {
          window.dispatchEvent(new CustomEvent('coach-open-add-content'));
        }}
      />

      <CoachProComingSoonDialog open={proDialogOpen} onOpenChange={setProDialogOpen} defaultEmail={user?.email ?? ''} />
      <CoachHelpButton onReplayTour={() => { resetTour(); setTourOpen(true); }} />
    </div>
  );
}
